import { LocationList } from "./lotes.interfaces";

/** Representa un lote de exportación con datos de cliente, material, almacenamiento y CFDI. */
export interface ExportationBatch {
    /** Número de registro del lote. */
    registry_number?:                        number;
    /** Identificador del BL del lote. */
    bl_lot_id:                               number;
    /** Número de lote del cliente. */
    customer_lot_number:                     string;
    /** Identificador del cliente. */
    customer_id:                             number;
    /** Nombre del cliente. */
    client_name:                             string;
    /** Identificador de la razón social. */
    id_business_name:                        number;
    /** Fecha de atraque (ISO 8601). */
    docking_date:                            string;
    /** Fecha de desatraque (ISO 8601). */
    undocking_date:                          string;
    /** Identificador del grupo de material. */
    material_group_id:                       number;
    /** Nombre del grupo de material. */
    material_group_name:                     string;
    /** Identificador del material. */
    material_id:                             number;
    /** Nombre del material. */
    material_name:                           string;
    /** Identificador del empaque. */
    packing_id:                              number;
    /** Descripción del empaque. */
    packaging:                               string;
    /** Observaciones del lote. */
    observations:                            string;
    /** Tonelaje del lote. */
    tonnage:                                 number;
    /** Cantidad de piezas. */
    quantity_pieces:                         number;
    /** Identificador de la ubicación. */
    location_id:                             number;
    /** Días de almacenamiento gratuito. */
    days_free_storage:                       number;
    /** Tarifa de almacenamiento. */
    storage_fee:                             number;
    /** Tarifa especial de almacenamiento. */
    special_storage_rate:                    number;
    /** Nombre del consignatario. */
    consignee_name:                          string;
    /** Domicilio del consignatario. */
    address_consignee:                       string;
    /** Valor comercial. */
    commercial_value:                        number;
    /** Embarcador/receptor. */
    shipper_receiver:                        string;
    /** Domicilio del embarcador/receptor. */
    address_shipper_receiver:                string;
    /** Registros previos de toma de muestras. */
    previous_registration_sample_collection?: PreviousRegistrationSampleCollection[];
    /** Registro de daños o violaciones. */
    damage_violation_record:                 string;
    /** Mercancía embargada por aduana. */
    customs_embargoed_merchandise?:           CustomsEmbargoedMerchandise[];
    /** Identificación de la mercancía almacenada. */
    identification_stored_merchandise:       string;
    /** Volumen ocupado por la mercancía. */
    volume_occupied_merchandise:             number;
    /** Indica si la mercancía está en área fiscalizada. */
    merchandise_fiscalized_area:             boolean;
    /** Número PAMA. */
    pama_number:                             string;
    /** Folio fiscal del CFDI. */
    fiscal_folio_cfdi:                       string;
    /** Monto total del CFDI. */
    total_amount_cfdi:                       string;
    /** Monto total de la presentación. */
    total_amount_presentation:               string;
    /** Monto del depósito de mercancía. */
    amount_merchandise_deposit:              string;
    /** Monto de compensación. */
    compensation_amount:                     string;
    /** Domicilio. */
    address:                                 string;
    /** Dimensión del contenedor. */
    container_dimension:                     string;
    /** Tipo de contenedor. */
    container_type:                          string;
    /** Número de sellos del contenedor. */
    number_container_seals:                  string;
    /** Número de candados del contenedor. */
    number_container_locks:                  string;
    /** Contenedor sin consolidar. */
    unconsolidated_container:                string;
    /** Almacén de desconsolidación. */
    deconsolidated_store:                    string;
    /** Medio de transporte sin consolidar. */
    unconsolidated_means_transport:          string;
    /** Contenedor consolidado. */
    consolidated_container:                  string;
    /** Almacén de consolidación. */
    consolidated_store:                      string;
    /** Medio de transporte consolidado. */
    consolidated_means_transport:            string;
    /** Clave/ruta del archivo del lote de exportación. */
    file_export_batch:                       string;
    /** Toneladas de entrada. */
    input_tons?:                              number;
    /** Toneladas de salida. */
    output_tons?:                             number;
    /** Toneladas restantes. */
    remaining_tons?:                          number;
}

/** Mercancía embargada por aduana con su oficio, fecha, peso y archivo. */
export interface CustomsEmbargoedMerchandise {
    /** Número de oficio. */
    trade_number:                 string;
    /** Fecha del oficio (ISO 8601). */
    office_date:                  string;
    /** Peso de la mercancía embargada. */
    weight_embargoed_merchandise: number;
    /** Clave/ruta del archivo soporte. */
    file:                         string;
}

/** Registro previo de toma de muestras con oficio, cadena y peso de muestra. */
export interface PreviousRegistrationSampleCollection {
    /** Número de oficio. */
    trade_number:  string;
    /** Cadena identificadora del muestreo. */
    sampling_string: string;
    /** Peso de la muestra. */
    sample_weight: number;
}

/** Respuesta de creación de un lote de exportación. */
export interface ResponseCreateBatchExportation {
    /** Número de registro del lote creado. */
    registry_number: number;
}

/** Respuesta de archivo de un lote de exportación. */
export interface FileBatchExportation {
  /** URL para acceder al archivo. */
  url?:            string;
  /** Indica si la operación produjo error. */
  error?:          boolean;
  /** Mensaje descriptivo del resultado. */
  message?:        string;
  /** Contenido del archivo en base64. */
  file_base64?:    string;
}

/** Entrada de mercancía de un lote de exportación, con pesos, piezas y transporte. */
export interface ExportEntry {
    /** Identificador de la entrada. */
    id?:                  number;
    /** Identificador del lote de exportación. */
    export_batch_id:      number;
    /** Identificador del tipo de entrada. */
    entry_type_id?:       number;
    /** Clave del precinto. */
    precinct_key:         number;
    /** Placas del tractor. */
    tractor_plates:       string;
    /** Identificador del tipo de transporte. */
    transport_type_id:    number | string;
    /** Identificador del remolque. */
    trailer_id:           number | string;
    /** Peso de origen. */
    origin_weight:        number;
    /** Peso bruto. */
    gross_weight:         number;
    /** Peso de tara. */
    tare_weight:          number;
    /** Peso neto. */
    net_weight:           number;
    /** Placa de la caja/van. */
    van_plate:            string;
    /** Número de folio del ticket. */
    folio_ticket_number:  string;
    /** Peso de origen (segunda pesada). */
    origin_weight2:       number;
    /** Peso bruto (segunda pesada). */
    gross_weight2:        number;
    /** Peso de tara (segunda pesada). */
    tare_weight2:         number;
    /** Peso neto (segunda pesada). */
    net_weight2:          number;
    /** Placa de la caja/van (segunda pesada). */
    van_plate2:           string;
    /** Número de folio del ticket (segunda pesada). */
    folio_ticket_number2: string;
    /** Peso total. */
    total_weight:         number;
    /** Clave/ruta del documento del ticket. */
    ticket_document:      string;
    /** Nombre del checador. */
    checker_name:         string;
    /** Nombre del operador. */
    operator_name:        string;
    /** Línea transportista. */
    transport_line:       string | number;
    /** Fecha de admisión (ISO 8601). */
    admission_date:       string;
    /** Número de piezas. */
    number_pieces :       number;
    /** Número de piezas (segunda pesada). */
    number_pieces2:       number;
    /** Total de piezas. */
    total_pieces  :       number;
    /** Citas asociadas. */
    appointments?:        string;
    /** Identificador de la ubicación. */
    location_id:          number;
    /** Identificador de la báscula de origen. */
    original_scale_id?:   number;
    /** Indica si está asignado. */
    is_assigned?:         boolean;
    /** Nombre del barco. */
    ship_name?:           string;
    /** Número del barco. */
    ship_number?:         number;
    /** Lista de pesadas asociadas. */
    weight_list?:         any[];
}

/** Respuesta genérica de una eliminación. */
export interface deleteResponse {
    /** Indica si la operación produjo error. */
    error?:          boolean;
    /** Mensaje descriptivo del resultado. */
    message:        string;
}

/** Respuesta de operaciones sobre una entrada. */
export interface EntryResponse {
    /** Identificador de la entrada afectada. */
    id: number;
    /** Indica si la operación produjo error. */
    error:          boolean;
    /** Mensaje descriptivo del resultado. */
    message:        string;
}

/** Referencia a la URL de un archivo de entrada de exportación. */
export interface FileEntryExportation {
    /** URL para acceder al archivo. */
    url:            string;
}

/** Lote disponible para transferencia con su identificador y nombre. */
export interface BatchesAvailableTransfer {
    /** Identificador del lote. */
    id:                 number,
    /** Nombre del lote. */
    lot_name:           string
}

/** Carta membretada con su agencia aduanal y lote de exportación. */
export interface MembratedLetter {
    /** Agencia aduanal. */
    customs_agency:     string;
    /** Identificador del lote de exportación. */
    id_export_batch:    number;
}

/** Salida de mercancía de un lote de exportación o cabotaje. */
export interface ExportOutput {
    /** Identificador de la salida. */
    id?:                        number;
    /** Identificador del tipo de salida. */
    output_type_option_id:      number;
    /** Descripción del tipo de salida. */
    outputDescription?:         string;
    /** Identificador del lote de exportación. */
    export_batch_id?:           number;
    /** Identificador del lote de cabotaje. */
    cabotage_batch_id?:         number;
    /** Nombre del checador. */
    checker_name:               string;
    /** Identificador del destino. */
    destination_id?:            number | string;
    /** Clave del precinto. */
    precinct_key:               number;
    /** Fecha de salida (ISO 8601). */
    departure_date:             string;
    /** Número del barco. */
    ship_number:                number | string;
    /** Nombre del barco. */
    ship_name:                  string;
    /** Tonelaje total. */
    total_tonnage?:             number;
    /** Total de piezas. */
    total_pieces?:              number;
    /** Destinatario. */
    addressee:                  string;
    /** Empresa de destino. */
    destination_company:        string;
    /** Número de solicitud. */
    request_number:             number;
    /** Agente aduanal. */
    customs_agent:              string;
    /** Número de retiro. */
    withdrawal_number:          string;
    /** Persona a la que se transfiere. */
    person_to_transfer?:        string;
    /** Clave/ruta del archivo de la salida. */
    entrie_output_file?:        string;
    /** Báscula de origen. */
    original_scale?:            number;
    /** Identificador del tipo de transporte. */
    transport_type_id?:         number;
    /** Clave/ruta del archivo de salida de cabotaje. */
    cabotage_output_file?:      string;
    /** Destino. */
    destiny?:                   string;
    /** Lista de pesadas asociadas. */
    weight_list?:               any[];
    /** Identificador de la ubicación. */
    location_id?:               number;
    /** Tonelaje asignado. */
    assigned_tonnage?:          number;
    /** Lista de ubicaciones asociadas. */
    location_list:              LocationList[]
}

/** Tipo de salida de exportación con su descripción. */
export interface ExportOutputType {
    /** Identificador del tipo de salida. */
    id:          number;
    /** Descripción del tipo de salida. */
    description: string;
}

/** Respuesta de operaciones sobre una salida. */
export interface OuputResponse {
    /** Identificador de la salida afectada. */
    id:              number;
    /** Indica si la operación produjo error. */
    error?:          boolean;
    /** Mensaje descriptivo del resultado. */
    message:        string;
}

/** Pesada de transporte de salida de exportación. */
export interface ExportTransportOutput {
  /** Identificador de la lista de pesos de exportación. */
  id_export_weight_list: number;
  /** Peso bruto. */
  gross_weight:          number;
  /** Peso de tara. */
  tare_weight:           number;
  /** Peso neto. */
  net_weight:            number;
  /** Cantidad de piezas. */
  quantity_pieces:       number;
  /** Placa del transporte. */
  plate:                 string;
  /** Número de ticket. */
  no_ticket:             string;
  /** Número de slab (nulo si no aplica). */
  no_slab:               null;
  /** Número de colada (nulo si no aplica). */
  no_cast:               null;
  /** Número de BL. */
  no_bl:                 string;
}
